/*
 Credentials.ts
 =========

------------------------------------------------------------------------
 Author:    Oskar Truffer                                         2025
 Modifs:

 Goal:   Demonstrator credential manager
         for FUOTA status tracking & management for TRACE.

   (c) 2025  Oskar Truffer   
   --------------------------

   CSEM S.A.
   Jaquet-Droz 1
   CH-2000 Neuchâtel
   http://www.csem.ch


   THIS SOFTWARE IS CONFIDENTIAL AND CANNOT BE DISTRIBUTED
   WITHOUT THE CSEM PRIOR WRITTEN AGREEMENT.

   THIS  SOFTWARE CAN ONLY BE USED IN THE EUROPEAN PROJECT TRACE AND
   SHALL REMAIN PRIVATE AT ALL TIMES.

------------------------------------------------------------------------
*/

import { CognitoIdentityCredentialProvider, fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import awsConfig from "../aws-config.json";
import { AwsCredentialIdentity } from "@smithy/types";
import { injectable, inject } from 'inversify';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Auth  } from "aws-amplify";
import {UserService} from './UserService'

interface CustomJwtPayloadWithRole extends JwtPayload {
    "cognito:roles"?: string;
   }

@injectable()
export class Credentials {
    @inject(UserService)
    public userService!: UserService;
    private creds: Map<string, {expiration: number, credentialsProvider: CognitoIdentityCredentialProvider}> = new Map();

    /**
     * 
     * @param roleArn The role we want to assume, needs to be assumably via a web identity.
     * @returns A cached credentials provider per role arn.
     */
    async getProvider(roleArn?: string, roleArnDetails?: string) : Promise<CognitoIdentityCredentialProvider> {
        const session = await Auth.currentSession();
        if (session == null) {
            throw new Error("We try to assume a role but do not have a session.");
        }

        if(roleArn == undefined) {
            roleArn = await this.userService.getFittingRoleArn(roleArnDetails)
        }

        if(roleArn == undefined) {
            throw new Error("No role to assume given and no preferred role could be identified.");
        }

        console.log(this.creds.has(roleArn))
        console.log(this.creds.has(roleArn) ? this.creds.get(roleArn)!.expiration: null)
        console.log(new Date().getTime() / 1000)

        // if (!this.creds.has(roleArn) ||  this.creds.get(roleArn)!.expiration <= new Date().getTime() / 1000) { --> this shows sometimes the borring error
            const c = await this.getFreshProvider(roleArn);
            this.creds.set(roleArn, c);
        // }

        return this.creds.get(roleArn)!.credentialsProvider;
    }

    async getFreshProvider(roleArn: string): Promise<{expiration: number, credentialsProvider: CognitoIdentityCredentialProvider}> {
        const session = await Auth.currentSession();
        if (session == null) {
            throw new Error("We try to assume a role but do not have a session.");
        }
        
        let idToken = session.getIdToken().getJwtToken();
        let COGNITO_ID = `cognito-idp.${awsConfig.aws_cognito_region}.amazonaws.com/${awsConfig.aws_user_pools_id}`; // 'COGNITO_ID' has the format 'cognito-idp.REGION.amazonaws.com/COGNITO_USER_POOL_ID'
        let loginData = {
        [COGNITO_ID]: idToken,
        };

        console.log("Getting fresh provider...");
        return {
            expiration: session.getIdToken().getExpiration(),
            credentialsProvider:  fromCognitoIdentityPool({
                clientConfig: { region: awsConfig.aws_cognito_region },
                identityPoolId: awsConfig.aws_cognito_identity_pool_id,
                customRoleArn: roleArn,
                logins: loginData,
            })
        };
    }
    
    async get(roleArn?: string, roleArnDetails?: string): Promise<AwsCredentialIdentity> {
        return (await this.getProvider(roleArn,roleArnDetails))();
    }

}